import type { DateSelectArg, EventApi } from '@fullcalendar/core';
import type { Absence, ResourceUser } from '@src/features/absence';
import { formatIsoDate, isNextDay, subtractDay, toTime } from '@src/shared/lib';
import type { SelectedAbsence, TimelineSlot, VisibleRange } from '../types';
import {
  formatDurationLabel,
  getAbsencesIdsForRange,
  getEventLabel,
  getUserAbsences,
} from './absence';
import {
  ABSENCE_BLOCK_POSITION_END,
  ABSENCE_BLOCK_POSITION_SINGLE,
  ABSENCE_BLOCK_POSITION_START,
} from './constants';
import { getUserWorktimeForDate } from './resources';

const FIXED_MONTH_DAY_COUNT = 31;

export function getFixedMonthVisibleRange(
  anchorDate: Date,
  dayCount: number = FIXED_MONTH_DAY_COUNT,
): VisibleRange {
  const start = new Date(
    anchorDate.getFullYear(),
    anchorDate.getMonth(),
    anchorDate.getDate(),
  );
  const end = new Date(start);
  end.setDate(end.getDate() + dayCount);

  return { start, end };
}

/**
 * Where the window goes, and how much of it there has to be.
 *
 * A month is the width the calendar opens on, not a limit: a run longer than
 * that gets a window long enough to hold it and the timeline scrolls. Showing
 * part of a run and letting somebody decide on it is the thing worth avoiding
 * -- what they approve would not be what they were looking at.
 */
export type FocusWindow = { start: Date; days: number };

/**
 * FullCalendar's ScrollGrid throttles layout passes to 10 per
 * SCROLLGRID_RESIZE_INTERVAL (500 ms) and silently drops the rest -- including
 * the forced ones `updateSize()` triggers. When a burst of re-renders outruns
 * that budget the last, correct render never gets measured: the resource area
 * keeps its natural row heights while the timeline lanes fall back to
 * `innerHeight: ''` and collapse, so only rows holding an event stay visible.
 * Re-measuring once the burst is over repairs it -- the same trick FullCalendar
 * itself uses in ResourceTimelineView.ensureScrollGridResize.
 */
export const SCROLLGRID_SETTLE_MS = 600;

/**
 * Whether the block behind a selection may run past the range in memory.
 *
 * The absences on screen are fetched for the visible window plus a day either
 * side, so a run carrying on beyond it always shows a day at or past the
 * boundary -- the day after that was never loaded. A selection built from
 * those rows therefore names only the part that happens to be on screen, which
 * matters well beyond looking wrong: approving it would approve those days and
 * quietly leave the rest of the absence undecided.
 */
export function isSelectionClipped(
  selection: SelectedAbsence,
  visibleRange: VisibleRange,
): boolean {
  return clippedSides(selection, visibleRange) !== null;
}

function clippedSides(
  selection: SelectedAbsence,
  visibleRange: VisibleRange,
): 'start' | 'end' | 'both' | null {
  if (!selection.start || !selection.end) {
    return null;
  }
  const start = new Date(`${selection.start}T00:00:00`);
  const end = new Date(`${selection.end}T00:00:00`);
  const before = start < visibleRange.start;
  const after = end >= visibleRange.end;

  if (before && after) return 'both';
  if (before) return 'start';
  if (after) return 'end';
  return null;
}

/**
 * Where to put the window so a clipped selection is shown whole.
 *
 * Normally a week before the absence, the same run-up a deep link opens with,
 * so it is read in the context of the days around it rather than starting
 * flush against the edge.
 *
 * The window is a fixed 31 days, so a long run cannot afford the week. Then it
 * takes the whole window, pinned to whichever end is still hidden: a run
 * overhanging the right edge gets pinned to its first day, one overhanging the
 * left gets its last known day put at the far end. Either way the days that
 * were missing come back in one move rather than one day at a time.
 *
 * The window keeps its usual width here, unlike a run named by a task
 * reference. A selection built from loaded rows has been cut off at whichever
 * edge it overran, so its own end says nothing about how far the run really
 * goes -- sizing a window to it would size it to the truncation. One window at
 * a time, and the caller asks again once the days it revealed have arrived.
 *
 * Returns null when nothing needs to move, or when the run is longer than the
 * window can hold at all and moving would only trade one clipped end for the
 * other.
 */
export function getFocusWindowStart(
  selection: SelectedAbsence,
  visibleRange: VisibleRange,
): Date | null {
  const sides = clippedSides(selection, visibleRange);
  if (sides === null || sides === 'both') {
    return null;
  }

  const first = new Date(`${selection.start}T00:00:00`);
  const last = new Date(`${selection.end}T00:00:00`);
  if (daysCovered(first, last) > FIXED_MONTH_DAY_COUNT) {
    return null;
  }

  let anchor = addDays(first, -LEAD_IN_DAYS);
  if (daysCovered(anchor, last) > FIXED_MONTH_DAY_COUNT) {
    anchor =
      sides === 'start' ? addDays(last, -(FIXED_MONTH_DAY_COUNT - 1)) : first;
  }

  const start = new Date(
    anchor.getFullYear(),
    anchor.getMonth(),
    anchor.getDate(),
  );
  return start.getTime() === visibleRange.start.getTime() ? null : start;
}

/**
 * The window that holds ``first`` through ``last``, or null if one already is.
 *
 * A run that fits the usual month keeps the usual week of run-up. One that
 * does not gets exactly the width it needs, starting where it starts: there is
 * no run-up to spare, and the days that matter are the ones in the run.
 *
 * Only safe where both ends are known for certain -- which means a task
 * reference, since it carries them.
 */
function windowHolding(
  first: Date,
  last: Date,
  visibleRange: VisibleRange,
): FocusWindow | null {
  const loadedThrough = addDays(visibleRange.end, -1);
  if (first >= visibleRange.start && last <= loadedThrough) {
    // Already all in memory. Worth checking before anything else: every range
    // change rebuilds the grid, and the rebuild is what outruns ScrollGrid's
    // layout budget.
    return null;
  }

  const runLength = daysCovered(first, last);
  const fitsWithRunUp = runLength + LEAD_IN_DAYS <= FIXED_MONTH_DAY_COUNT;
  const anchor = fitsWithRunUp ? addDays(first, -LEAD_IN_DAYS) : first;
  const days = fitsWithRunUp ? FIXED_MONTH_DAY_COUNT : runLength;

  return {
    start: new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()),
    days,
  };
}

/**
 * Where to put the window so every day a task reference names is loaded.
 *
 * Worked out from the reference's own first and last day rather than from what
 * resolved, which is the whole point: a run reaching past the window resolves
 * to the days inside it and looks complete, so a selection can never be
 * trusted to say how far it was meant to reach.
 *
 * A run longer than the month the calendar opens on gets a window as long as
 * it needs to be; the timeline scrolls. Showing part of a run would let
 * somebody approve days they never saw.
 *
 * Returns null when the window already holds it, which is the signal that
 * everything the reference names has been asked for.
 */
export function getReferenceWindowStart(
  firstIsoDate: string,
  lastIsoDate: string,
  visibleRange: VisibleRange,
): FocusWindow | null {
  const first = new Date(`${firstIsoDate}T00:00:00`);
  const last = new Date(`${lastIsoDate}T00:00:00`);
  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) {
    return null;
  }
  return windowHolding(first, last, visibleRange);
}

/** How many days a window has to span to hold both ends inclusively. */
function daysCovered(first: Date, last: Date): number {
  const day = 24 * 60 * 60 * 1000;
  return Math.round((last.getTime() - first.getTime()) / day) + 1;
}

function addDays(value: Date, days: number): Date {
  const shifted = new Date(value);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
}

/**
 * Days of run-up left before an absence the calendar is being opened on.
 *
 * A week, so the absence is read in the context of the days around it rather
 * than starting flush against the edge. The remaining three weeks of the
 * window are room for it to run into; anything longer gives up the run-up
 * rather than the absence.
 */
const LEAD_IN_DAYS = 7;

/**
 * The 31-day window the calendar opens on.
 *
 * `anchorIsoDate` lets a deep link (a task reference naming an absence in
 * another month) seed the range up front, so the calendar mounts where it is
 * wanted instead of jumping there imperatively a frame later.
 *
 * With an anchor the window opens just before it rather than on the first of
 * its month, which is what keeps a deep link to a single range change. A month
 * is a poor window for something that does not respect months: an absence
 * starting on the 28th has three days of it left to run into and spills out,
 * and moving the window afterwards to take in the rest costs a second grid
 * rebuild -- the one that outruns ScrollGrid's layout budget and collapses the
 * lanes for a moment. Opening a few days early leaves nearly the whole window
 * ahead of it, so there is usually nothing to correct.
 */
export function getInitialVisibleRange(
  anchorIsoDate?: string | null,
): VisibleRange {
  const anchor = anchorIsoDate
    ? new Date(`${anchorIsoDate}T00:00:00`)
    : new Date();

  if (anchorIsoDate && !Number.isNaN(anchor.getTime())) {
    return getFixedMonthVisibleRange(addDays(anchor, -LEAD_IN_DAYS));
  }

  const fallback = Number.isNaN(anchor.getTime()) ? new Date() : anchor;
  return getFixedMonthVisibleRange(
    new Date(fallback.getFullYear(), fallback.getMonth(), 1),
  );
}

/**
 * Collects absence details for a given absence ID.
 *
 * Retrieves key attributes from the targeted absence record and, if available,
 * from its associated neighbors (whole activity).
 *
 * @param {number} absenceId
 *        Unique identifier of the absence to collect.
 * @param {Array<Absence>} absences
 *        List of absence objects to search through.
 * @param {boolean} single
 *        Gets data for single absenece object with specified id or
 *        for all absences if it is multiday absence event
 */
export function getAbsenceSelectionById(
  absenceId: number,
  absences: Absence[],
  single = false,
): SelectedAbsence | null {
  if (!absenceId || !Array.isArray(absences) || absences.length === 0) {
    return null;
  }

  const anchor = absences.find((a) => a.id === absenceId);
  if (!anchor) {
    return null;
  }

  if (
    single ||
    !anchor.allDay ||
    anchor.blockPosition === ABSENCE_BLOCK_POSITION_SINGLE
  ) {
    return hydrateSelectedAbsence(
      {
        userId: anchor.userId,
        start: formatIsoDate(anchor.date),
        end: formatIsoDate(anchor.date),
        startTime: anchor.startTime,
        endTime: anchor.endTime,
        absenceIds: [anchor.id],
      },
      absences,
    );
  }

  const block = getAbsenceBlock(anchor, absences);
  if (!block.length) {
    return null;
  }

  return hydrateSelectedAbsence(
    {
      userId: block[0].userId,
      start: formatIsoDate(block[0].date),
      end: formatIsoDate(block[block.length - 1].date),
      startTime: null,
      endTime: null,
      absenceIds: block.map((a) => a.id),
    },
    absences,
  );
}

/**
 * The run of days a withdrawal request covers, around `anchor`.
 *
 * The server flags every day it named, not just the one the task points at, so
 * the run is read off the flags rather than guessed at from the block. It can
 * be a slice out of the middle of a longer entry -- days being given back
 * because a change is replacing them, say -- which is why the walk stops at the
 * first unflagged day either side.
 */
/**
 * Selection for the days a task reference names.
 *
 * The reference carries them, so they are taken as given rather than worked
 * out from the calendar: a withdrawal request can name a slice out of the
 * middle of an entry, and by the time anyone follows the link the entry may
 * have been cut about by another decision. Days that have since gone are
 * dropped, and a reference naming none of what is loaded selects nothing.
 */
export function getAbsenceReferenceSelection(
  absenceIds: number[],
  absences: Absence[],
): SelectedAbsence | null {
  const named = absenceIds
    .map((id) => absences.find((absence) => absence.id === id))
    .filter((absence): absence is Absence => absence !== undefined)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const [first] = named;
  if (!first) {
    return null;
  }

  // A part-day absence stands alone and keeps its times; a run of whole days
  // is one bar from the first to the last.
  if (named.length === 1 && !first.allDay) {
    return hydrateSelectedAbsence(
      {
        userId: first.userId,
        start: formatIsoDate(first.date),
        end: formatIsoDate(first.date),
        startTime: first.startTime,
        endTime: first.endTime,
        absenceIds: [first.id],
      },
      absences,
    );
  }

  const last = named[named.length - 1];
  return hydrateSelectedAbsence(
    {
      userId: first.userId,
      start: formatIsoDate(first.date),
      end: formatIsoDate(last.date),
      startTime: null,
      endTime: null,
      absenceIds: named.map((absence) => absence.id),
    },
    absences,
  );
}

/**
 * Whether two days are proposing to replace the same entry.
 *
 * Two proposals can be identical in everything the calendar draws -- same
 * type, same tentativeness, the same approval outstanding, lying on
 * consecutive days -- and still be two questions, because each is written
 * over an entry of its own and is answered on its own. What they replace is
 * the only thing that tells them apart, and the server sends it along.
 *
 * Days that are not proposals both carry `null` here and so group as before.
 */
function sameChangeOrigin(a: Absence, b: Absence) {
  const left = a.changedFrom ?? null;
  const right = b.changedFrom ?? null;
  if (left === null || right === null) {
    return left === right;
  }
  return (
    left.typeId === right.typeId &&
    left.dateFrom === right.dateFrom &&
    left.dateTo === right.dateTo &&
    left.startTime === right.startTime &&
    left.endTime === right.endTime
  );
}

export function getAbsenceBlock(
  anchor: Absence,
  absences: Absence[],
): Absence[] {
  const sameGroup = (a: Absence) => {
    return (
      anchor.typeId != null &&
      a.userId === anchor.userId &&
      a.allDay &&
      a.typeId === anchor.typeId &&
      a.absenceTentative === anchor.absenceTentative &&
      a.approvedManager === anchor.approvedManager &&
      a.approvedHr === anchor.approvedHr &&
      sameChangeOrigin(a, anchor)
    );
  };

  const candidates = absences
    .filter(sameGroup)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const anchorIdx = candidates.findIndex((a) => a.id === anchor.id);
  if (anchorIdx === -1) {
    return [];
  }

  let left = anchorIdx;
  while (
    left - 1 >= 0 &&
    isNextDay(candidates[left - 1].date, candidates[left].date)
  ) {
    left -= 1;
  }

  let right = anchorIdx;
  while (
    right + 1 < candidates.length &&
    isNextDay(candidates[right].date, candidates[right + 1].date)
  ) {
    right += 1;
  }

  return candidates.slice(left, right + 1);
}

/**
 * The entry a selection is reshaping, where it says so unambiguously.
 *
 * A drag on the timeline knows only dates, so unlike the edit pane it names
 * no absence: the slot carries what the range happens to cover and nothing
 * about what is being edited. Where that lands on one entry of the type
 * being submitted, the reshape is not in doubt and this answers with the
 * entry -- the same thing the edit pane would have said.
 *
 * The whole entry comes back, days outside the selection included, because
 * those are precisely the days a reshape drops. Anything the geometry leaves
 * open is left alone instead: a selection reaching into two entries has no
 * one answer, and a different type is a retype rather than a reshape, which
 * is written over what is there and asked about as a change.
 *
 * An entry running to the edge of the loaded window is left alone too. Only
 * the days in the window are in hand, so a run flush against one of its ends
 * may carry on outside it -- and reshaping what can be seen of an entry would
 * leave the rest of it booked on the days being moved away from, which is
 * worse than not recognising the reshape at all.
 */
function reachesLoadedEdge(
  entry: Absence[],
  loaded: LoadedRange | null,
): boolean {
  const first = entry[0];
  const last = entry[entry.length - 1];
  if (!loaded || !first || !last) return true;

  // Against the days actually asked for, which only the caller knows: the
  // calendar and a one-off fetch ask for different spans, and working the
  // bounds out from one of them here made every entry beginning on the first
  // day of the other look complete when it had simply been cut off there.
  if (first.dateStr <= loaded.from || last.dateStr >= loaded.to) return true;

  // What the calendar draws: a squared-off end says the run carries on into
  // days that are not here. The same thing the dates say above, read off the
  // shape the user is looking at.
  const opensHere =
    first.blockPosition === ABSENCE_BLOCK_POSITION_START ||
    first.blockPosition === ABSENCE_BLOCK_POSITION_SINGLE;
  const closesHere =
    last.blockPosition === ABSENCE_BLOCK_POSITION_END ||
    last.blockPosition === ABSENCE_BLOCK_POSITION_SINGLE;
  return !opensHere || !closesHere;
}

/**
 * The days in hand, both ends inclusive, as the ISO days they were asked
 * for.
 *
 * Days rather than instants on purpose. An absence is a calendar day and its
 * ``date`` is parsed from ``YYYY-MM-DD``, which lands on UTC midnight, while
 * the calendar's own bounds are built as local midnight -- so east of UTC an
 * absence on the first day loaded compares as *later* than that boundary and
 * a run cut off there passes for a whole one. Comparing the written days
 * instead has no timezone in it at all.
 */
export type LoadedRange = { from: string; to: string };

export type ReshapeResolution =
  /** The entry is in hand and whole. */
  | { status: 'entry'; absences: Absence[] }
  /** It reads as a reshape, but the run leaves the loaded days -- load
   * ``anchor``'s surroundings and ask again. */
  | { status: 'clipped'; anchor: Absence }
  /** Not a reshape of anything. */
  | { status: 'none' };

/**
 * Every entry of the submitted type that ``rows`` reach into, whole.
 *
 * Rows of another type are passed over rather than refused: those days are
 * being written over as something else, which is a different question and
 * one the days around them have no part in. What is left is this type's, and
 * each run it belongs to comes back entire -- the days outside the request
 * are the ones it is being asked to give up.
 *
 * Every run reached is answered for, not just one, because the server
 * reshapes them all: a request covering two runs of a type means both end up
 * inside it. Answering with one of them would leave the other's days being
 * given back with nothing on screen having said so.
 */
function entriesContaining(
  rows: Absence[],
  absences: Absence[],
  typeId: unknown,
): Absence[] | null {
  const mine = rows.filter(
    (absence) => absence.allDay && absence.typeId === typeId,
  );
  if (!mine.length) return null;

  const seen = new Set<number>();
  const entries: Absence[] = [];
  for (const row of mine) {
    if (seen.has(row.id)) continue;
    for (const member of getAbsenceBlock(row, absences)) {
      if (seen.has(member.id)) continue;
      seen.add(member.id);
      entries.push(member);
    }
  }
  return entries.sort((a, b) => a.dateStr.localeCompare(b.dateStr));
}

export function getReshapedEntry(
  slot: TimelineSlot | null,
  absences: Absence[],
  typeId: unknown,
  loaded: LoadedRange | null,
): ReshapeResolution {
  const selectedIds = slot?.selectedIds ?? [];
  if (!selectedIds.length) return { status: 'none' };

  const covered = absences.filter((absence) =>
    selectedIds.includes(absence.id),
  );
  const entries = entriesContaining(covered, absences, typeId);
  if (!entries) return { status: 'none' };
  if (reachesLoadedEdge(entries, loaded)) {
    return { status: 'clipped', anchor: entries[0] };
  }
  return { status: 'entry', absences: entries };
}

export type EditedEntry = {
  /** What to submit as `edited_absence_ids`. */
  ids: number[];
  /** The rows behind those ids, as far as they are loaded. */
  absences: Absence[];
  /**
   * Set when a selection reads as a reshape of an entry that runs past the
   * days in hand. Nothing can be submitted from that: load this row's
   * surroundings and resolve again against them.
   */
  clipped?: Absence;
  /**
   * Set when the pane names an entry whose rows are not all in hand. The ids
   * are still right -- they are never rebuilt from what is loaded -- but
   * nothing can say what those days hold, so what a reshape would remove
   * cannot be put in front of the user until they are fetched.
   */
  needsRows?: boolean;
};

/**
 * The entry a submit is editing: the one the pane opened, or failing that the
 * one the selection plainly reshapes.
 *
 * The opened ids are taken as they are and never rebuilt from the rows in
 * hand. The calendar holds a window, and moving about it takes rows out of
 * memory without taking the entry out of the edit -- rebuilt, the ids would
 * come back empty, the server would read a reshape as a fresh booking, and
 * the days being moved away from would quietly stay where they are.
 *
 * The rows are returned alongside for what needs to read them, and are empty
 * when the entry is out of the window. That is the honest answer: nothing
 * here can say what a row it cannot see used to hold.
 */
export function getEditedEntry(
  slot: TimelineSlot | null,
  absences: Absence[],
  typeId: unknown,
  { infer, loaded }: { infer: boolean; loaded: LoadedRange | null },
): EditedEntry {
  const openedIds =
    slot?.editedAbsenceIds ??
    (slot?.editedAbsenceId ? [slot.editedAbsenceId] : []);
  if (openedIds.length) {
    const rows = absences.filter((absence) => openedIds.includes(absence.id));
    if (rows.length !== openedIds.length) {
      return { ids: openedIds, absences: rows, needsRows: true };
    }

    // Opened on part of an entry, of the type being submitted: the entry is
    // what is being reshaped, exactly as it is when the days are dragged
    // rather than opened. Selecting three days of a week and asking for them
    // is asking for the week to be those three days -- read as the days
    // themselves it says nothing at all, and used to be written over with a
    // copy of what was already there.
    const entries = entriesContaining(rows, absences, typeId);
    if (entries && entries.length > rows.length) {
      return reachesLoadedEdge(entries, loaded)
        ? { ids: openedIds, absences: rows, clipped: entries[0] }
        : { ids: entries.map((absence) => absence.id), absences: entries };
    }
    return { ids: openedIds, absences: rows };
  }
  if (!infer) return { ids: [], absences: [] };

  const resolved = getReshapedEntry(slot, absences, typeId, loaded);
  if (resolved.status === 'clipped') {
    return { ids: [], absences: [], clipped: resolved.anchor };
  }
  if (resolved.status === 'none') return { ids: [], absences: [] };
  return {
    ids: resolved.absences.map((absence) => absence.id),
    absences: resolved.absences,
  };
}

// Local-time construction on purpose: `new Date('YYYY-MM-DD')` parses as
// UTC midnight, which formats as the previous day anywhere west of UTC.
export function localDateFromIso(day: string): Date {
  const [year, month, dayOfMonth] = day.split('-').map(Number);
  return new Date(year, month - 1, dayOfMonth);
}
function isoDayAfter(day: string): string {
  const date = localDateFromIso(day);
  date.setDate(date.getDate() + 1);
  return formatIsoDate(date);
}
export function isoDaysBetween(from: Date, to: Date): string[] {
  const days: string[] = [];
  let day = formatIsoDate(from);
  const last = formatIsoDate(to);
  while (day <= last) {
    days.push(day);
    day = isoDayAfter(day);
  }
  return days;
}
export function isoRuns(days: string[]): Array<[string, string]> {
  const runs: Array<[string, string]> = [];
  [...days].sort().forEach((day) => {
    const lastRun = runs[runs.length - 1];
    if (lastRun && isoDayAfter(lastRun[1]) === day) {
      lastRun[1] = day;
    } else {
      runs.push([day, day]);
    }
  });
  return runs;
}

/**
 * Whether the days asked for are not the days the entry already has.
 *
 * Every day, not the two ends. What is being edited can be more than one run
 * -- a request reaching two entries of a type reshapes both -- and then the
 * ends can agree while the days between them do not: asking for 20th to 30th
 * over runs of 20th-24th and 28th-30th starts and finishes exactly where
 * they do and still adds three days in the middle.
 */
export function hasRangeChanged(
  entry: Absence[],
  from: Date,
  to: Date,
): boolean {
  if (!entry.length) return false;
  const covered = new Set(entry.map((absence) => absence.dateStr));
  const requested = isoDaysBetween(from, to);
  return (
    requested.length !== covered.size ||
    requested.some((day) => !covered.has(day))
  );
}

/**
 * Whether a submit leaves the entry it names exactly as it is.
 *
 * The days it already has, of the type and tentativeness it already has:
 * there is nothing for anyone to decide, so the server writes the notes and
 * asks nobody -- see ``_keep_as_it_is``. Telling the user an approver has to
 * agree to it would describe a change that is not going to happen.
 */
export function leavesEntryUnchanged(
  entry: Absence[],
  typeId: unknown,
  tentative: boolean,
  from: Date,
  to: Date,
): boolean {
  if (!entry.length) return false;
  if (
    entry.some(
      (absence) =>
        absence.typeId !== typeId || absence.absenceTentative !== tentative,
    )
  ) {
    return false;
  }
  return !hasRangeChanged(entry, from, to);
}

export function hydrateSelectedAbsence(
  selection: SelectedAbsence,
  absences: Absence[],
): SelectedAbsence {
  if (!selection.absenceIds.length || !absences.length) {
    return selection;
  }

  const snapshotAbsences = selection.absenceIds
    .map((id) => absences.find((absence) => absence.id === id))
    .filter((absence): absence is Absence => !!absence);

  if (!snapshotAbsences.length) {
    return selection;
  }

  const primaryAbsenceId =
    selection.primaryAbsenceId ?? selection.absenceIds[0];
  const primaryAbsence =
    snapshotAbsences.find((absence) => absence.id === primaryAbsenceId) ??
    snapshotAbsences[0];
  const blockHead =
    primaryAbsence.allDay &&
    primaryAbsence.blockPosition !== ABSENCE_BLOCK_POSITION_SINGLE
      ? (getAbsenceBlock(primaryAbsence, absences)[0] ?? primaryAbsence)
      : primaryAbsence;

  return {
    ...selection,
    primaryAbsenceId: primaryAbsence.id,
    typeId: primaryAbsence.typeId ?? null,
    absenceTentative: primaryAbsence.absenceTentative ?? false,
    approvedManager: primaryAbsence.approvedManager ?? null,
    approvedHr: primaryAbsence.approvedHr ?? null,
    notes: blockHead.notes ?? primaryAbsence.notes ?? '',
    notesHr: blockHead.notesHr ?? primaryAbsence.notesHr ?? '',
    snapshotAbsences,
  };
}

export function getSlotDatefromSelection(
  info: DateSelectArg,
  absences: Absence[],
): TimelineSlot {
  const end = info.allDay ? subtractDay(info.end, 1) : info.end;
  const userId = info.resource?.extendedProps.userId;
  const userAbsences = getUserAbsences(userId, absences);
  const startTime = info.allDay ? null : toTime(info.start);
  const endTime = info.allDay ? null : toTime(info.end);
  const slot: TimelineSlot = {
    resourceId: info?.resource?.id,
    start: formatIsoDate(info.start),
    end: formatIsoDate(end),
    startTime,
    endTime,
    selectedIds: getAbsencesIdsForRange(info.start, end, userAbsences, {
      dayView: !info.allDay,
      startTime,
      endTime,
    }),
    editedAbsenceId: null,
    userId: info.resource?.extendedProps.userId,
  };
  return slot;
}

export function getSlotFromEvent(event: EventApi): TimelineSlot | null {
  if (!event.start) return null;
  if (!event.end) return null;

  const absenceId = Number(event.extendedProps.absenceId ?? event.id);
  const end = event.allDay ? subtractDay(event.start, 1) : event.end;
  return {
    resourceId: event.getResources()[0].id,
    start: formatIsoDate(event.startStr),
    end: formatIsoDate(end),
    startTime: event.allDay ? null : toTime(event.start),
    endTime: event.allDay ? null : toTime(event.end),
    selectedIds: [absenceId],
    editedAbsenceId: absenceId,
    userId: event.getResources()[0].extendedProps.userId,
  };
}

/*
   Returns an array of free dates  within the slot range that are
   not occupied by absences.
*/
export function getSlotFreeDates(
  slot: TimelineSlot,
  absences: Absence[],
): Date[] {
  if (!slot || !slot.start || !slot.end) {
    return [];
  }

  if (slot.end < slot.start) {
    return [];
  }
  const start = slot.start;
  const end = slot.end;

  const occupied = new Set(
    (absences || [])
      .filter((a) => !(a.dateStr < start) && !(a.dateStr > end))
      .map((a) => a.dateStr),
  );
  const free = [];
  let cur = new Date(slot.start);

  while (cur <= new Date(slot.end)) {
    if (!occupied.has(formatIsoDate(cur))) {
      free.push(cur);
    }
    const nextDate = cur.getDate() + 1;
    cur = new Date(cur);
    cur.setDate(nextDate);
  }
  return free;
}

export function getTransientEventsForSlot(
  slot: TimelineSlot,
  absences: Array<Absence>,
  users: Array<ResourceUser>,
  dayView = false,
) {
  if (!slot || !slot.start || !slot.end || !slot.userId) {
    return [];
  }
  const userAbsences = getUserAbsences(slot.userId, absences);
  if (dayView) {
    if (slot.editedAbsenceId) {
      return [];
    }
    const overlappingIds = getAbsencesIdsForRange(
      slot.start,
      slot.end,
      userAbsences,
      {
        dayView: true,
        startTime: slot.startTime,
        endTime: slot.endTime,
        excludeIds: slot.editedAbsenceId ? [slot.editedAbsenceId] : [],
      },
    );
    if (overlappingIds.length > 0) {
      return [];
    }

    const userWorktime = getUserWorktimeForDate(slot.userId, users, slot.start);
    const startTime = slot.startTime || userWorktime?.start;
    const endTime = slot.endTime || userWorktime?.end;
    const label =
      startTime && endTime
        ? formatDurationLabel(startTime, endTime)
        : getEventLabel(slot.start, slot.end, true);
    return [
      {
        id: `transient-event-${slot.start}`,
        resourceId: slot.resourceId,
        start: formatIsoDate(slot.start, startTime),
        end: formatIsoDate(slot.end, endTime),
        classNames: [
          'transient-event',
          'transient-edge-left',
          'transient-edge-right',
        ],
        extendedProps: { label: label },
        startEditable: false,
        durationEditable: false,
        resourceEditable: false,
      },
    ];
  }

  const slotFreeDates = getSlotFreeDates(slot, userAbsences);
  if (!slotFreeDates.length) {
    return [];
  }
  // const user = users.find((u) => u.id === slot.userId);
  // const worktime = user.worktime;

  const transientEvents = slotFreeDates.map((day, idx) => {
    const label = getEventLabel(day, day, dayView);
    const isLast = idx === slotFreeDates.length - 1;
    const event = {
      id: `transient-event-${formatIsoDate(day)}`,
      resourceId: slot.resourceId,
      start: formatIsoDate(day),
      end: formatIsoDate(day),
      classNames: [
        'transient-event',
        'transient-month-event',
        'transient-edge-left',
        isLast ? 'transient-edge-right' : 'transient-join-right',
      ],
      extendedProps: { label: label },
      editable: false,
    };

    return event;
  });
  return transientEvents;
}

/**
 * Absences the calendar should paint as the active selection.
 *
 * Editing reshapes one row, so the edited absence stays lit wherever it moves.
 * Withdrawing is the opposite: the edited ids only bound how far the withdrawal
 * may reach, so the live range in `selectedIds` is what should light up -- the
 * day that happened to be clicked must not stay selected once the range moves
 * off it.
 */
export function getActiveAbsenceIds(slot: TimelineSlot | null): number[] {
  if (!slot) return [];
  if (slot.withdrawing) return slot.selectedIds;
  return slot.editedAbsenceId ? [slot.editedAbsenceId] : slot.selectedIds;
}

export function isAbsenceSelected(
  slot: TimelineSlot,
  absences: Absence[],
): slot is TimelineSlot & { start: string; end: string; userId: number } {
  if (!slot.start || !slot.end || !slot.userId || !slot.selectedIds.length) {
    return false;
  }

  const userAbsences = getUserAbsences(slot.userId, absences);
  if (getSlotFreeDates(slot, userAbsences).length > 0) {
    return false;
  }

  const selectedIds = [...slot.selectedIds].sort((a, b) => a - b);

  const selectedAbsences = selectedIds
    .map((id) => absences.find((absence) => absence.id === id))
    .filter((absence): absence is Absence => !!absence);

  if (selectedAbsences.length !== selectedIds.length) {
    return false;
  }

  const [firstAbsence] = selectedAbsences;
  if (!firstAbsence) {
    return false;
  }

  const groupedSelection = getAbsenceSelectionById(firstAbsence.id, absences);
  const singleSelection = getAbsenceSelectionById(
    firstAbsence.id,
    absences,
    true,
  );

  const isWithinGroupedSelection = groupedSelection
    ? slot.userId === groupedSelection.userId &&
      slot.start >= groupedSelection.start &&
      slot.end <= groupedSelection.end
    : false;

  const allSameType = selectedAbsences.every(
    (absence) =>
      firstAbsence.typeId != null &&
      absence.userId === firstAbsence.userId &&
      absence.typeId === firstAbsence.typeId &&
      absence.absenceTentative === firstAbsence.absenceTentative &&
      absence.approvedManager === firstAbsence.approvedManager &&
      absence.approvedHr === firstAbsence.approvedHr &&
      absence.allDay === firstAbsence.allDay,
  );

  const matchesTimes = selectedAbsences.every(
    (absence) =>
      absence.startTime === firstAbsence.startTime &&
      absence.endTime === firstAbsence.endTime,
  );

  const matchesSingleSelection =
    !!singleSelection &&
    slot.start === singleSelection.start &&
    slot.end === singleSelection.end &&
    slot.startTime === singleSelection.startTime &&
    slot.endTime === singleSelection.endTime;

  return (
    isWithinGroupedSelection &&
    allSameType &&
    (matchesTimes || matchesSingleSelection)
  );
}

export function isFullyOccupiedSlot(
  slot: TimelineSlot,
  absences: Absence[],
): slot is TimelineSlot & { start: string; end: string; userId: number } {
  if (!slot.start || !slot.end || !slot.userId || !slot.selectedIds.length) {
    return false;
  }

  const userAbsences = getUserAbsences(slot.userId, absences);
  if (getSlotFreeDates(slot, userAbsences).length > 0) {
    return false;
  }

  const isSingleDay = slot.start === slot.end;
  if (isSingleDay) {
    return true;
  }

  const selectedAbsences = slot.selectedIds
    .map((id) => absences.find((a) => a.id === id))
    .filter((a): a is Absence => !!a);
  const first = selectedAbsences[0];

  return (
    !!first &&
    selectedAbsences.every(
      (a) =>
        a.typeId === first.typeId &&
        a.userId === first.userId &&
        a.absenceTentative === first.absenceTentative &&
        a.approvedManager === first.approvedManager &&
        a.approvedHr === first.approvedHr &&
        a.allDay === first.allDay,
    )
  );
}
